<template>
  <div class="iar-submissions-table" data-cy="submissions-table">
    <div
      class="grid-control__container"
      data-cy="submissions-table-control-container"
    >
      <UITabContainer
        class="submissions-tab-container"
        :tabs="tabs"
        :defaultTabId="tabChoice"
        @tab-changed="tabChanged"
      >
        <template v-slot:OpenJobs></template>
        <template v-slot:MyOpenJobs></template>
        <template v-slot:AllJobs></template>
      </UITabContainer>

      <div class="pull-right">
        <div class="col-sm-4">
          <input
            type="text"
            class="form-control jobid-search__input"
            v-model="searchJobId"
            placeholder="Search by Job Id"
          />
        </div>

        <Button
          :theme-color="'primary'"
          type="button"
          @click="filterByJobId"
          data-cy="by-jobid"
          >By Job Id</Button
        >

        <div>
          <DateRangePicker
            :value="dateRange"
            :max="new Date()"
            :popupSettings="{
              animate: true,
              anchorAlign: {
                horizontal: 'center',
                vertical: 'bottom',
              },
              popupAlign: {
                horizontal: 'center',
                vertical: 'top',
              },
            } as any"
            @change="handleChange"
          />
        </div>

        <Button
          :theme-color="'primary'"
          type="button"
          @click="filterByDate"
          data-cy="by-date"
          >By Date</Button
        >

        <ClearFiltersButton @clearFiltersClicked="clearFilters" />
      </div>
    </div>
    <SubmissionsGrid
      :submissionJobs="submissionJobs"
      :filter="filter"
      :loading="submissionJobsLoading"
      :skip="skip"
      :pageSize="take"
      :total="totalSubmissionJobs"
      :getPropValues="getPropValuesAsync"
      :tabChoice="tabChoice"
      :sort="sortDescriptors"
      @submissionsChanged="onSubmissionsChanged"
      @sortChanged="onSortChanged"
      @columnreorder="updateColumnMenuIcons"
      @columnssubmit="updateColumnMenuIcons"
    />
    <ColumnMenuIconHandler :filter="filter" ref="menuIconHandlerRef" />
  </div>
</template>

<script setup lang="ts">
import { api } from "@/api";
import { computed, onBeforeMount, ref } from "vue";
import { IarTab } from "@/Domain/IarTab";
import { Button } from "@progress/kendo-vue-buttons";
import { useMainStore } from "@/stores/useMainStore";
import SubmissionsGrid from "./SubmissionsGrid.vue";
import { CacheService } from "@/shared/services/CacheService";
import { createCacheKey } from "@/shared/helpers/cacheHelper";
import UITabContainer from "@/shared/ui/tabs/tab-container.vue";
import ClearFiltersButton from "@/shared/ui/inputs/ClearFiltersButton.vue";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import {
  dateTimeConvert,
  dateTimeConvertBack,
  FilterValuesTake,
  getJsonFilterString,
  valueFilterConvert,
} from "@/shared/helpers/filtersHelper";
import type { SelectionRange } from "@progress/kendo-vue-dateinputs";
import type { SubmissionsTable } from "@/Domain/SubmissionsTable";
import type { GridFilterChangeEvent } from "@progress/kendo-vue-grid";
import type { ItemSortChangedOptions } from "@/Domain/ItemChangedOptions";
import type { ValueFieldConvertOption } from "@/Domain/ValueFieldConvertOption";
import type { GetSubmissionsWithFilteringRequest } from "@/api/submissionsOverview";
import type {
  CompositeFilterDescriptor,
  FilterDescriptor,
  SortDescriptor,
} from "@progress/kendo-data-query";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import moment from "moment";
import ColumnMenuIconHandler from "@/shared/ui/filters/ColumnMenuIconHandler.vue";
import DateRangePicker from "@/shared/ui/inputs/DateRangePicker.vue";
import { GridOptionsService } from "@/shared/services/GridOptionsService";

enum EnumSearchBy {
  JobId,
  Date,
}

const tabs = [
  {
    id: IarTab.OpenJobs.toString(),
    label: "Open Jobs",
  },
  {
    id: IarTab.MyOpenJobs.toString(),
    label: "My Open Jobs",
  },
  {
    id: IarTab.AllJobs.toString(),
    label: "All Jobs",
  },
];

let activeTabId = new URLSearchParams(window.location.search).get("activeTab");
activeTabId = tabs.find((t) => t.id === activeTabId)?.id ?? tabs[0].id;

const tabChoice = ref<string>(activeTabId);

const main = useMainStore();

const defaultSortField = "DateProcessedForSort";

const submissionJobs = ref<SubmissionsTable[]>([]);
const totalSubmissionJobs = ref<number>(0);
const submissionJobsLoading = ref<boolean>(false);
const skip = ref<number>(0);
const take = ref<number>(500);

const searchJobId = ref<string>("");
const searchBy = ref<EnumSearchBy>(EnumSearchBy.Date);

const sortField = ref<string>(defaultSortField);
const sortAscending = ref<boolean>(true);

const menuIconHandlerRef = ref<InstanceType<
  typeof ColumnMenuIconHandler
> | null>(null);

const tableName = computed(() => "IARSubmissions_" + tabChoice.value);

const filter = ref<CompositeFilterDescriptor>(
  GridOptionsService.loadGridFilter(tableName.value)
);


const valueFiltersConvertOptions = [
  {
    column: "DocumentDate",
    convert: dateTimeConvert,
    convertBack: dateTimeConvertBack,
  },
  {
    column: "DateProcessedForSort",
    convert: dateTimeConvert,
    convertBack: dateTimeConvertBack,
  },
  {
    column: "CheckedOutDateForSort",
    convert: dateTimeConvert,
    convertBack: dateTimeConvertBack,
  },
  {
    column: "WfLastUpdateDateForSort",
    convert: dateTimeConvert,
    convertBack: dateTimeConvertBack,
  },
  {
    column: "ApprovalDateForSort",
    convert: dateTimeConvert,
    convertBack: dateTimeConvertBack,
  },
] as ValueFieldConvertOption[];

const sortDescriptors = computed(() => {
  return (
    sortField.value
      ? [
          {
            field: sortField.value,
            dir: sortAscending.value ? "asc" : "desc",
          },
        ]
      : []
  ) as SortDescriptor[];
});

const currentDate = new Date();
const openJobsStartDate = moment().subtract(1, "years").toDate();
const allJobsStartDate = moment().subtract(1, "months").toDate();

const dateRange = ref({
  start: new Date(openJobsStartDate),
  end: new Date(currentDate),
} as SelectionRange);

const handleChange = function (newRange: SelectionRange) {
  dateRange.value = newRange;
};

const tabChanged = async function (tab: string) {
  tabChoice.value = tab;

  const pageSize = await GridOptionsService.getPageSizeAsync(tableName.value);
  if (pageSize) {
    take.value = pageSize;
  } else {
    if (tab === IarTab.OpenJobs.toString()) take.value = 500;
    else take.value = 100;
  }

  if (
    tab === IarTab.AllJobs.toString() ||
    tab == IarTab.MyOpenJobs.toString()
  ) {
    sortField.value = "WfLastUpdateDateForSort";
    sortAscending.value = false;
  } else {
    sortField.value = defaultSortField;
    sortAscending.value = true;
  }

  if (tab === IarTab.AllJobs.toString())
    dateRange.value.start = allJobsStartDate;
  else dateRange.value.start = openJobsStartDate;

  filter.value = GridOptionsService.loadGridFilter(tableName.value);

  getSubmissionsAsync();
};

const getSubmissionsWithFilteringAsync = async function (
  valuesForFieldOnly: string = ""
) {
  const requestParams = {
    userLogin: "ALL", // need to set this param to "ALL" (instead of user's login) to see list of all jobs
    iarLogin: "", // need to set this param to blank (instead of user's login) to see list of all jobs
    companyLogin: "0",
    status: "OPEN",
    allJobTypes: 0,
    showCheckedOutJobs: 1,
    beginDate: dateRange.value.start?.toLocaleDateString("en-US"),
    endDate: dateRange.value.end?.toLocaleDateString("en-US"),
    jobId: searchBy.value == EnumSearchBy.JobId ? searchJobId.value : "",
    filters: getJsonFilterString(filter.value, valueFiltersConvertOptions),
    take: take.value,
    skip: skip.value,
    sortField: sortField.value,
    ascending: sortAscending.value,
    valuesForFieldOnly: valuesForFieldOnly,
  } as GetSubmissionsWithFilteringRequest;

  if (tabChoice.value === IarTab.OpenJobs.toString())
    requestParams.showCheckedOutJobs = 0; // for 'Open Jobs' set this param to 0 to hide Checked Out Jobs

  if (tabChoice.value === IarTab.MyOpenJobs.toString())
    requestParams.iarLogin = main.userLogin; // for 'My Jobs' set this param to user's login to see list of iar person's jobs

  if (tabChoice.value === IarTab.AllJobs.toString())
    requestParams.status = "ALL"; // for 'All Jobs' set this param to "ALL" to see list of all jobs

  if (!valuesForFieldOnly)
    return api.get("GetSubmissionsWithFiltering", requestParams);

  requestParams.take = FilterValuesTake;
  requestParams.skip = 0;

  return CacheService.getAsync(createCacheKey(requestParams), () =>
    api.get("GetSubmissionsWithFiltering", requestParams)
  );
};

const getSubmissionsAsync = async function () {
  if (submissionJobsLoading.value) return;
  submissionJobsLoading.value = true;

  await handleResponsePromiseAsync(
    getSubmissionsWithFilteringAsync(),
    (data) => {
      submissionJobs.value = data.ListItems;
      totalSubmissionJobs.value = data.TotalItemsInList;
    },
    "SubmissionsTable.vue",
    "getSubmissionsAsync"
  );

  submissionJobsLoading.value = false;
};

const getPropValuesAsync = async function (column: GridColumnPropsExtended) {
  let result: SubmissionsTable[] = [];
  await handleResponsePromiseAsync(
    getSubmissionsWithFilteringAsync(column.field),
    (data) => {
      result = data.ListItems;
      valueFilterConvert(valueFiltersConvertOptions, column.field!, result);
    },
    "SubmissionsTable.vue",
    "getPropValuesAsync"
  );
  return result;
};

const onSubmissionsChanged = function (
  skipValue: number,
  takeValue: number,
  filterEvent: GridFilterChangeEvent | null
) {
  if (filterEvent) {
    filter.value.filters = filterEvent.filter.filters;
    GridOptionsService.saveGridFilter(tableName.value, filter.value);
  }
  if (take.value != takeValue) {
    take.value = takeValue;
    GridOptionsService.savePageSizeAsync(tableName.value, takeValue);
  }
  skip.value = skipValue;
  getSubmissionsAsync();
};

const onSortChanged = function (options: ItemSortChangedOptions) {
  sortField.value = options.field;
  sortAscending.value = options.ascending;
  skip.value = 0;
  getSubmissionsAsync();
};

const filterByJobId = function () {
  searchBy.value = EnumSearchBy.JobId;
  getSubmissionsAsync();
};

const filterByDate = function () {
  searchBy.value = EnumSearchBy.Date;
  getSubmissionsAsync();
};

const clearFilters = function () {
  console.debug("SubmissionsTable.vue, clearFilters()");
  filter.value = {
    logic: "and",
    filters: [{} as FilterDescriptor],
  };

  GridOptionsService.saveGridFilter(tableName.value, filter.value);
  sortField.value = "";
  sortAscending.value = false;
  getSubmissionsAsync();
};

const updateColumnMenuIcons = function () {
  setTimeout(() => {
    menuIconHandlerRef.value?.updateColumnMenuIcons();
  }, 100);
};

onBeforeMount(async () => {
  // Call tabChanged() in case the user navigates back to this page with active tab parameter
  tabChanged(tabChoice.value);
});
</script>

<style lang="scss">
.iar-submissions-table {
  .grid-control__container {
    display: flex;
    align-items: flex-end;
    padding: 10px 10px 0;
  }

  .pull-right {
    display: flex;
    margin-left: auto;
    gap: 8px;
    align-items: flex-end;
    padding-bottom: 10px;
    flex-wrap: wrap;
  }

  .form-link {
    text-decoration: none;
  }
}
</style>
